import { useState, useEffect } from 'react';
import { Download, Upload, Database, AlertTriangle, CheckCircle, RefreshCw, HardDrive, Shield, Image, FolderArchive, Link2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BackupManager() {
  const [stats, setStats] = useState({});
  const [galleryStats, setGalleryStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [imageExportLoading, setImageExportLoading] = useState(false);
  const [imageImportLoading, setImageImportLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [restoreMode, setRestoreMode] = useState('replace');
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchGalleryStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/backup/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchGalleryStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/gallery/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGalleryStats(res.data);
    } catch (err) {
      console.error('Failed to fetch gallery stats:', err);
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/backup`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'bigbear_backup.json';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'Backup downloaded successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to create backup. Please try again.' });
    } finally {
      setBackupLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.json')) {
        setMessage({ type: 'error', text: 'Please select a valid JSON backup file.' });
        return;
      }
      setSelectedFile(file);
      setShowRestoreConfirm(true);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    
    setRestoreLoading(true);
    setMessage(null);
    setRestoreResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('merge_mode', restoreMode);
      
      const response = await axios.post(`${API}/admin/restore`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setRestoreResult(response.data);
      setMessage({ type: 'success', text: 'Database restored successfully!' });
      fetchStats(); // Refresh stats
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Restore failed. Please try again.' });
    } finally {
      setRestoreLoading(false);
      setShowRestoreConfirm(false);
      setSelectedFile(null);
    }
  };

  const handleImageExport = async () => {
    setImageExportLoading(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/gallery/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'gallery_images.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'Gallery images exported successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to export images. Please try again.' });
    } finally {
      setImageExportLoading(false);
    }
  };

  const handleImageImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      setMessage({ type: 'error', text: 'Please select a ZIP file.' });
      return;
    }
    
    setImageImportLoading(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/gallery/import`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setMessage({ type: 'success', text: `Imported ${response.data.imported} images successfully! Click "Sync Database" to update the gallery.` });
      fetchGalleryStats(); // Refresh stats
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Import failed. Please try again.' });
    } finally {
      setImageImportLoading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleSyncGallery = async () => {
    setSyncLoading(true);
    setMessage(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/gallery/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Build detailed success message
      const data = response.data;
      
      // Check if no images found
      if (data.files_found === 0) {
        setMessage({ 
          type: 'warning', 
          text: 'No images found to sync. Please import images first using "Import Images (ZIP)" before syncing.' 
        });
        fetchGalleryStats();
        return;
      }
      
      let successMsg = `Gallery synced! Created ${data.entries_created} entries for ${data.files_found} images.`;
      
      // Add homepage update info
      if (data.homepage_updated) {
        const homepageItems = [];
        if (data.homepage_updated.hero) homepageItems.push('hero');
        if (data.homepage_updated.about_images > 0) homepageItems.push(`${data.homepage_updated.about_images} about`);
        if (data.homepage_updated.gallery_preview > 0) homepageItems.push(`${data.homepage_updated.gallery_preview} gallery preview`);
        if (data.homepage_updated.cta_background) homepageItems.push('CTA');
        if (homepageItems.length > 0) {
          successMsg += ` Homepage images restored: ${homepageItems.join(', ')}.`;
        }
      }
      
      // Add property update info
      if (data.property_updated) {
        const propItems = [];
        if (data.property_updated.booking_page_image) propItems.push('booking page');
        if (data.property_updated.hero_image) propItems.push('property hero');
        if (propItems.length > 0) {
          successMsg += ` Property images restored: ${propItems.join(', ')}.`;
        }
      }
      
      setMessage({ type: 'success', text: successMsg });
      fetchGalleryStats();
      fetchStats();
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Sync failed';
      let errorMsg = errorDetail;
      
      // Provide more helpful error messages
      if (errorDetail.includes('No image files') || errorDetail.includes('Gallery folder not found')) {
        errorMsg = 'No images found on server. Please import your images first using "Import Images (ZIP)" button.';
      }
      
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSyncLoading(false);
      setShowSyncConfirm(false);
    }
  };

  const collectionLabels = {
    inquiries: 'Inquiries',
    renters: 'Renters',
    contracts: 'Contracts',
    email_logs: 'Email Logs',
    email_templates: 'Email Templates',
    gallery: 'Gallery Images',
    amenities: 'Amenities',
    bedrooms: 'Bedrooms',
    pricing: 'Pricing',
    faq: 'FAQs',
    availability: 'Availability',
    property: 'Property Info',
    homepage_content: 'Homepage Content',
    settings: 'Settings',
    file_library: 'File Library'
  };

  const getTotalRecords = () => {
    return Object.values(stats).reduce((sum, count) => sum + count, 0);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Database className="w-7 h-7 text-emerald-700" />
          Database Backup & Restore
        </h1>
        <p className="text-gray-600 mt-1">
          Create backups of your data before deployments or restore from a previous backup.
        </p>
      </div>

      {/* Important Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-900">Important: Image Files</h3>
            <p className="text-amber-800 text-sm mt-1">
              <strong>Uploaded images are NOT preserved during deployment.</strong> Before deploying, export your gallery images 
              using the tool below. After deployment, import the ZIP file to restore your images.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900">About Your Database</h3>
            <p className="text-blue-800 text-sm mt-1">
              Your database is stored separately from the application code. Normal deployments will <strong>NOT</strong> erase 
              your inquiries, contracts, or other data. This backup tool is provided for extra peace of mind.
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 
          'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Gallery Images Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Image className="w-5 h-5 text-emerald-600" />
          Image Files (Gallery, Guest Services, Things to Do)
        </h2>
        
        {/* Gallery Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{galleryStats.files_on_disk || 0}</div>
            <div className="text-sm text-gray-600">Total Files</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-emerald-700">{galleryStats.gallery_files || 0}</div>
            <div className="text-sm text-emerald-600">Gallery</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-700">{galleryStats.guest_services_files || 0}</div>
            <div className="text-sm text-blue-600">Guest Services</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-amber-700">{galleryStats.things_to_do_files || 0}</div>
            <div className="text-sm text-amber-600">Things to Do</div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{galleryStats.database_entries || 0}</div>
            <div className="text-sm text-gray-600">Gallery DB Entries</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{galleryStats.total_size_mb || 0} MB</div>
            <div className="text-sm text-gray-600">Total Size</div>
          </div>
        </div>

        {/* Status Warning */}
        {galleryStats.status === 'missing_files' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-800 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <strong>Warning:</strong> No image files found on server. Import your images below.
            </p>
          </div>
        )}

        {/* Mismatch Warning */}
        {galleryStats.gallery_files > 0 && galleryStats.gallery_files !== galleryStats.database_entries && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-amber-800 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <strong>Mismatch detected:</strong> {galleryStats.gallery_files} gallery files on server but {galleryStats.database_entries} database entries. 
              Click "Sync Database" to fix.
            </p>
          </div>
        )}

        {/* Export/Import/Sync Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={handleImageExport}
            disabled={imageExportLoading || galleryStats.files_on_disk === 0}
            className="flex items-center justify-center gap-2 bg-emerald-700 text-white py-3 px-4 rounded-lg font-medium hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {imageExportLoading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <FolderArchive className="w-5 h-5" />
                Export Images
              </>
            )}
          </button>
          
          <label className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium cursor-pointer transition-colors ${
            imageImportLoading 
              ? 'bg-gray-300 cursor-not-allowed' 
              : 'bg-amber-500 text-white hover:bg-amber-600'
          }`}>
            <input
              type="file"
              accept=".zip"
              onChange={handleImageImport}
              disabled={imageImportLoading}
              className="hidden"
            />
            {imageImportLoading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Import Images
              </>
            )}
          </label>

          <button
            onClick={() => setShowSyncConfirm(true)}
            disabled={syncLoading || galleryStats.files_on_disk === 0}
            className="flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncLoading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <Link2 className="w-5 h-5" />
                Sync Database
              </>
            )}
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          <strong>Workflow:</strong> 1) Export images from preview → 2) Deploy → 3) Import images on live site → 4) Click "Sync Database" to update gallery entries
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Current Data Stats */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <HardDrive className="w-5 h-5 text-gray-600" />
              Current Database
            </h2>
            <button 
              onClick={fetchStats}
              className="text-gray-500 hover:text-gray-700 p-1"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading stats...</div>
          ) : (
            <>
              <div className="mb-4 p-3 bg-emerald-50 rounded-lg">
                <div className="text-2xl font-bold text-emerald-700">{getTotalRecords().toLocaleString()}</div>
                <div className="text-sm text-emerald-600">Total Records</div>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Object.entries(stats).map(([key, count]) => (
                  <div key={key} className="flex justify-between items-center py-1.5 px-2 hover:bg-gray-50 rounded">
                    <span className="text-gray-600 text-sm">{collectionLabels[key] || key}</span>
                    <span className="font-medium text-gray-900">{count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-6">
          {/* Backup Card */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
              <Download className="w-5 h-5 text-emerald-600" />
              Create Backup
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Download a complete backup of all your data as a JSON file. 
              Recommended before any major changes or deployments.
            </p>
            <button
              onClick={handleBackup}
              disabled={backupLoading}
              className="w-full bg-emerald-700 text-white py-3 px-4 rounded-lg font-medium hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {backupLoading ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Creating Backup...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Download Backup
                </>
              )}
            </button>
          </div>

          {/* Restore Card */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
              <Upload className="w-5 h-5 text-amber-600" />
              Restore from Backup
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Upload a previously downloaded backup file to restore your data.
            </p>
            
            {/* Restore Mode Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Restore Mode</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="replace"
                    checked={restoreMode === 'replace'}
                    onChange={(e) => setRestoreMode(e.target.value)}
                    className="text-emerald-600"
                  />
                  <span className="text-sm">
                    <strong>Replace</strong> - Clear existing data and restore from backup
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="restoreMode"
                    value="merge"
                    checked={restoreMode === 'merge'}
                    onChange={(e) => setRestoreMode(e.target.value)}
                    className="text-emerald-600"
                  />
                  <span className="text-sm">
                    <strong>Merge</strong> - Only add records that don't exist
                  </span>
                </label>
              </div>
            </div>

            <label className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 px-4 text-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors block">
              <input
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
              <span className="text-gray-600 text-sm">Click to select backup file (.json)</span>
            </label>
          </div>
        </div>
      </div>

      {/* Restore Result */}
      {restoreResult && (
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Restore Results</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(restoreResult.restored || {}).map(([key, count]) => (
              <div key={key} className="bg-gray-50 rounded-lg p-3">
                <div className="text-lg font-bold text-emerald-700">{count}</div>
                <div className="text-xs text-gray-600">{collectionLabels[key] || key}</div>
              </div>
            ))}
          </div>
          {restoreResult.backup_info && (
            <div className="mt-4 text-sm text-gray-500">
              Backup created: {new Date(restoreResult.backup_info.created_at).toLocaleString()}
              {restoreResult.backup_info.created_by && ` by ${restoreResult.backup_info.created_by}`}
            </div>
          )}
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Confirm Restore</h3>
            </div>
            
            <p className="text-gray-600 mb-2">
              You are about to restore from: <strong>{selectedFile?.name}</strong>
            </p>
            
            {restoreMode === 'replace' ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-red-800 text-sm">
                  <strong>Warning:</strong> Replace mode will delete all existing data before restoring. 
                  This action cannot be undone.
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-blue-800 text-sm">
                  Merge mode will only add new records. Existing data will not be modified.
                </p>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRestoreConfirm(false);
                  setSelectedFile(null);
                }}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRestore}
                disabled={restoreLoading}
                className={`flex-1 py-2 px-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 ${
                  restoreMode === 'replace' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'bg-emerald-600 hover:bg-emerald-700'
                } disabled:opacity-50`}
              >
                {restoreLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Restoring...
                  </>
                ) : (
                  'Restore Now'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Confirmation Modal */}
      {showSyncConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Link2 className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Sync Gallery Database</h3>
            </div>
            
            <p className="text-gray-600 mb-4">
              This will update the gallery database to match the actual image files on the server.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-amber-800 text-sm">
                <strong>Warning:</strong> This will replace all existing gallery entries. 
                Category assignments will be reset to "exterior". You may need to re-categorize images after syncing.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowSyncConfirm(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSyncGallery}
                disabled={syncLoading}
                className="flex-1 py-2 px-4 rounded-lg text-white font-medium flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {syncLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  'Sync Now'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
